import { computed, h, defineComponent, ref, SetupContext, watch } from 'vue';
// @ts-ignore
import { BTaginput } from 'buefy/dist/esm/taginput';
import { t } from '@/ui/i18n/i18n';
import { SelectOption, SelectOptions, SelectOptionsWithTooltip, SelectOptionWithTooltip } from '@/ui/types';
import { cloneDeep } from 'lodash';
import { tooltip } from '@/ui/components/Tooltip';
import { featureExplanationIcon } from '@/ui/components/icon/icons';

export interface GroupOptions<I extends SelectOption> {
  type: string;
  items: I[];
}

export interface TagInputProps<T = number> {
  value: T[];
  options: SelectOptions<T> | SelectOptionsWithTooltip<T> | GroupOptions<SelectOption>[];
  disabled: boolean;
  icon?: string;
  placeholder?: string;
  groupField?: string;
  groupOptions?: string;
  name?: string;
  nonVisibleOptions?: SelectOptions<T>;
  maxTags?: number;
  maxListedOptions?: number;
}

export const TagInput = defineComponent<TagInputProps>({
  name: 'TagInput',
  props: ['value', 'icon', 'options', 'disabled', 'placeholder', 'groupField', 'groupOptions', 'name',
    'nonVisibleOptions', 'maxTags', 'maxListedOptions'],
  setup(props, context: SetupContext) {
    const filterString = ref('');

    const selectedInternally = ref<Array<number>>(props.value);

    const filterSingleOption = (option: SelectOption) => !selectedInternally.value.includes(option.value);

    const filterGroupedOptions = (option: GroupOptions<SelectOption>) => {
      option.items = option.items.filter(filterSingleOption);

      return option;
    };

    const search = (option: SelectOption) => option.label
      .toLowerCase()
      .indexOf(filterString.value.trim().toLowerCase()) >= 0;

    const searchByGroupedBy = (option: GroupOptions<SelectOption>) => option
      .items
      .some(group => group.label.toLowerCase().indexOf(filterString.value.trim().toLowerCase()) >= 0);

    const isGroupedBy = () => props.groupField && props.groupOptions;

    const filteredOptions = computed(() => {
      let filtered = cloneDeep(props.options);

      //filter by search typing
      if (filterString.value.trim() !== '') {
        if (isGroupedBy()) {
          filtered = (filtered as GroupOptions<SelectOption>[]).filter(searchByGroupedBy);
        } else {
          filtered = (filtered as SelectOptions).filter(search);
        }
      }

      //filter out already selected options
      if (selectedInternally.value.length > 0) {
        if (isGroupedBy()) {
          filtered = (filtered as unknown as GroupOptions<SelectOption>[]).filter(filterGroupedOptions);
        } else {
          filtered = (filtered as SelectOptions).filter(filterSingleOption);
        }
      }

      return filtered.slice(0, props.maxListedOptions);
    });

    const findTagValueOption = (value: number) =>
      (([...props.options, ...(props.nonVisibleOptions ?? [])]) as SelectOptions)
        .find(option => option.value === value)!;

    const findGroupedTagValueOption = (value: number) =>
      (props.options as unknown as GroupOptions<SelectOption>[])
        .reduce((groupedOption: SelectOption, eachOption: GroupOptions<SelectOption>) => {
          const found = eachOption
            .items
            .find(option => option.value === value)!;
          return found || groupedOption;
        }, {} as SelectOption);

    //maps selected ids|strings into representation
    const tagValues = computed(() => {
      return props.value.map(value => {
        const valueOption: SelectOption = isGroupedBy()
          ? findGroupedTagValueOption(value)
          : findTagValueOption(value);

        //if input was a custom value, not selected from options, make it an option format
        const tagOption = valueOption !== undefined
          ? valueOption
          : {
            value,
            label: value.toString(),
          } as SelectOption;

        return tagOption;
      });
    });

    // init and update selectedInternally
    watch(() => props.value, value => {
      selectedInternally.value = value;
    });

    const disableChromeAutocomplete = {
      role: 'presentation',
      autocomplete: 'false',
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const instanceOfOptionWithTooltip = (option: any): option is SelectOptionWithTooltip => 'tooltip' in option;

    return function render() {
      return h(BTaginput, {
        props: {
          openOnFocus: true,
          data: filteredOptions.value,
          field: 'label',
          value: tagValues.value,
          // todo: it is better because it preserves order
          //  but on /users and some other urls it closes after 1st value select
          //value: props.value.map(v => props.options.find(option => option.value === v)),
          icon: props.icon,
          attached: true,
          autocomplete: true,
          ellipsis: true,
          groupField: props.groupField,
          groupOptions: props.groupOptions,
          maxtags: props.maxTags,
          hasCounter: false,
        },
        scopedSlots: {
          ...context.slots,
          default: context.slots.default
            ?? (({ option }) => instanceOfOptionWithTooltip(option)
              ? h('div', [
                option.label,
                tooltip({
                  label: option.tooltip,
                  multilined: true,
                  position: 'is-bottom',
                  appendToBody: true,
                }, [featureExplanationIcon({ classes: 'ml-2' })]),
              ])
              : h('span', option.label)
            ),
        },
        attrs: {
          ...disableChromeAutocomplete,
          disabled: props.disabled,
          placeholder: props.placeholder,
          name: props.name,
        },
        on: {
          input: (value: SelectOptions) => {
            context.emit('input', value.map(option => option.value));
            filterString.value = '';
          },
          typing: (value: string) => {
            filterString.value = value;
            context.emit('typing', value);
          },
          focus: (e: FocusEvent) => {
            context.emit('focus', e);
          },
          blur: (e: FocusEvent) => {
            context.emit('blur', e);
          },
          remove: (option: SelectOption) => {
            selectedInternally.value = selectedInternally.value.filter(v => v !== option.value);
          },
          //breaks filtering!
          // blur: () => { filter.value = ''; },
        },
      }, [
        h('template', { slot: 'empty' }, [
          context.slots.empty ? context.slots.empty() : h('span', t('form.no-matches')),
        ]),
        context.slots.header ? h('template', { slot: 'header' }, [
          context.slots.header(),
        ]) : null,
        context.slots.default ? context.slots.default() : null,
      ]);
    };
  },
});
